import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import React, { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

const useDepartureSortOptions = () => {
  const intl = useIntl()

  return useMemo(() => [
    {
      label: intl.formatMessage({
        id: 'Lq8XFWIDFuO4_xb3Zaw7',
        defaultMessage: 'Departure soon',
        description: 'Sort option label for departure date sorting in the cruises section',
      }),
      value: 'departure-soon',
    },
    {
      label: intl.formatMessage({
        id: 'iCkzbK9-UZA8fgFnqsQW',
        defaultMessage: 'Price: Low to high',
        description: 'Sort option label for ascending price sorting in the cruises section',
      }),
      value: 'price-asc',
    },
    {
      label: intl.formatMessage({
        id: 'I2iPqIKxfiLdEclyGtyw',
        defaultMessage: 'Price: High to low',
        description: 'Sort option label for descending price sorting in the cruises section',
      }),
      value: 'price-desc',
    },
  ], [intl])
}

export const DEFAULT_DEPARTURE_SORT_OPTION_VALUE = 'departure-soon'

interface Props {
  sort?: string
  onChange: (newSort: App.OfferListSortByOptions) => void
}

function CruiseDepartureSortDropdown({ onChange, sort }: Props) {
  const intl = useIntl()
  const [open, toggleSortOpen, , closeSort] = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const departureSortOptions = useDepartureSortOptions()

  const onSortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.value as App.OfferListSortByOptions)
    closeSort()
  }, [closeSort, onChange])

  const selectedLabel = useMemo(
    () => departureSortOptions.find(({ value }) => value === sort)?.label ?? departureSortOptions[0].label,
    [sort, departureSortOptions],
  )

  const sortPrefix = intl.formatMessage({
    id: '-spEtCR8aCEUOgWeCQ9f',
    defaultMessage: 'Sort: {selectedLabel}',
    description: 'Label for cruise departure sort dropdown chip showing selected sort option',
  }, { selectedLabel })

  return <>
    <AssistChip
      variant="filled"
      size="medium"
      onClick={toggleSortOpen}
      endIcon={<DropdownChevron open={open} />}
      ref={triggerRef}
    >
      {sortPrefix}
    </AssistChip>
    <DropdownList
      size="S"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      open={open}
      onClose={closeSort}
    >
      <RadioGroup direction="vertical" gap={16}>
        {departureSortOptions.map(option => <RadioInput
          value={option.value}
          onChange={onSortChange}
          key={option.value}
          checked={option.value === sort}
          name="sort"
        >
          {option.label}
        </RadioInput>)}
      </RadioGroup>
    </DropdownList>
  </>
}

export default React.memo(CruiseDepartureSortDropdown)
